import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismInput, PrismModal } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import GeneralFormFieldsCancelAndConfirm from 'lumen/components/general/form-fields/cancel-and-confirm';
import SettingsCurrencyConversionDropdownBaseCurrency from 'lumen/components/settings/currency-conversion/dropdowns/base-currency';
import type { Currency } from 'lumen/models';
import { APIGATEWAY } from 'lumen/utils/constants/general-constants';
import SilentTaskError, {
  getErrorMessage,
} from 'lumen/utils/silent-task-error';
import { timeout } from 'lumen/utils/waiters';

const TITLE_VALID_MAX_LENGTH = 30;

interface SettingsCurrencyConversionModalsCreateProfileSignature {
  Args: {
    isTitleUnique: (title: string) => boolean;
    onClose: () => void;
  };
}

export default class SettingsCurrencyConversionModalsCreateProfile extends Component<SettingsCurrencyConversionModalsCreateProfileSignature> {
  @service declare currencyConversion: Services['currency-conversion'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @tracked baseCurrency: Currency | null = null;
  @tracked title = '';

  createProfileTask = task(async (): Promise<void> => {
    if (this.baseCurrency === null) {
      return;
    }

    try {
      const newProfile =
        await this.currencyConversion.createExchangeRatesProfile({
          title: this.title.trim(),
          baseCurrency: this.baseCurrency,
        });

      this.toast.success(
        this.intl.t('settings.currency_conversion.profile_created', {
          title: newProfile.title,
        }),
        {
          showIcon: true,
        },
      );

      const routeName = this.currencyConversion.isHotelLevel
        ? 'hotel.settings.currency-conversion.profile'
        : 'market.settings.currency-conversion.profile';

      this.router.transitionTo(routeName, newProfile.id);

      this.usageAnalytics.track('Exchange Rate Profile Created', {
        eventTrigger: 'profile-created',
      });
    } catch (error) {
      this.toast.error(this.intl.t('general.error_short'), { showIcon: true });
      this.errorTracking.captureException(error);
      throw new SilentTaskError(getErrorMessage(error));
    }

    this.args.onClose();
  });

  debouncedUpdateTitleTask = task(
    { restartable: true },
    async (value: string): Promise<void> => {
      await timeout();
      this.title = value;
    },
  );

  fetchAllCurrenciesTask = task(
    { restartable: true },
    async (): Promise<Currency[]> => {
      const userId = this.hotelAndMarketService.user.id;

      try {
        return await this.store.query<Currency>('currency', {
          namespace: APIGATEWAY,
          typeKey: `users/${userId}/currencies`,
        });
      } catch (error) {
        this.errorTracking.captureException(error);
        throw new SilentTaskError(getErrorMessage(error));
      }
    },
  );

  get allCurrencies(): Currency[] {
    return this.fetchAllCurrenciesTask.last?.value ?? [];
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isConfirmDisabled() {
    return (
      !this.isTitleValid ||
      this.baseCurrency === null ||
      this.title.trim().length > TITLE_VALID_MAX_LENGTH ||
      this.createProfileTask?.isRunning
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTitleUnique() {
    return this.args.isTitleUnique(this.title.trim());
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isTitleValid() {
    return (
      this.isTitleUnique &&
      this.title.trim().length > 0 &&
      this.title.trim().length <= TITLE_VALID_MAX_LENGTH
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get titleErrorMessage() {
    return this.isTitleUnique
      ? undefined
      : this.intl.t('settings.currency_conversion.error.title_not_unique');
  }

  constructor(
    owner: unknown,
    args: SettingsCurrencyConversionModalsCreateProfileSignature['Args'],
  ) {
    // @ts-expect-error: Owner type mismatch between unknown and Glimmer Owner
    super(owner, args);
    void this.fetchAllCurrenciesTask.perform();
  }

  @action handleSelectBaseCurrency(currency: Currency): void {
    this.baseCurrency = currency;
  }

  @action onInputTitle(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    void this.debouncedUpdateTitleTask.perform(value);
  }

  <template>
    <PrismModal
      @isCloseDisabled={{this.createProfileTask.isRunning}}
      @onClose={{@onClose}}
    >
      <:headerContent>
        <h3 class="flex w-full items-center">
          {{t "settings.currency_conversion.create_new_profile"}}
        </h3>
      </:headerContent>

      <:default>
        <div class="modal--padded modal--small flex flex-col gap-400">
          <PrismInput
            @autofocus={{true}}
            @errorMessage={{this.titleErrorMessage}}
            @hasErrors={{not this.isTitleValid}}
            @label={{t "settings.currency_conversion.title"}}
            @maxLength={{30}}
            @onInput={{this.onInputTitle}}
            @placeholder={{t "settings.currency_conversion.title"}}
            @removePadding={{true}}
            @type="text"
            @value={{this.title}}
            data-test-field="title"
          />

          <div class="flex flex-col gap-100">
            <SettingsCurrencyConversionDropdownBaseCurrency
              @currencies={{this.allCurrencies}}
              @onSelect={{this.handleSelectBaseCurrency}}
              @selectedCurrency={{this.baseCurrency}}
            />
            <p class="text-neutral-subdued text-200--regular">
              {{t "settings.currency_conversion.base_currency_help"}}
            </p>
          </div>
        </div>
      </:default>

      <:footer>
        <div class="settings-modal__footer">
          <GeneralFormFieldsCancelAndConfirm
            @cancel={{@onClose}}
            @confirm={{this.createProfileTask.perform}}
            @confirmText={{t "settings.currency_conversion.create_profile"}}
            @isCancelDisabled={{this.createProfileTask.isRunning}}
            @isConfirmDisabled={{this.isConfirmDisabled}}
            @isConfirmLoading={{this.createProfileTask.isRunning}}
          />
        </div>
      </:footer>
    </PrismModal>
  </template>
}
